import React from 'react';
import { useState, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import Target from './Target';
import Fall from './Fall';

const BedroomLayout = ({
  pixelWidth,
  pixelHeight,
  sensorMounting,
  Region,
  Subregions,
  setSubregions,
  getMinMaxSensor,
  mod,
  active,
  target,
  regionBooleans,
  falls
}) => {

  const regionColor = 'rgb(153, 184, 224)';
  const regionBorderColor = 'rgb(0, 96, 208)';
  const subregionColor = 'rgb(153, 204, 153)';
  const subregionBorderColor = 'rgb(57, 153, 0)';

  const isSensorValid =
    sensorMounting !== null &&
    sensorMounting !== NaN &&
    sensorMounting !== 'Undefined' &&
    sensorMounting !== 'null';

  const showBedroom = sensorMounting !== undefined && sensorMounting !== 'Undefined';
  const M_TO_PX = pixelWidth / 4;
  const xSpan = Region.xMax - Region.xMin;
  const ySpan = Region.yMax - Region.yMin;

  let xMin = 0;
  let xMax = 0;
  let yMin = 0;
  let yMax = 0;
  let yShift = 0;
  let yShiftMargin = 0;
  let xShift = 0;
  let sensorRange;
  // sensorMounting: 0 - Wall, 2 - 45° Ceiling, we don't use 1 and 3
  const isCeiling = (sensorMounting === 2);
  if (showBedroom && isCeiling) {
    sensorRange = getMinMaxSensor(sensorMounting, Region.yMin, Region.yMax);
    xMin = -2;
    xMax = 2;
    yMin = sensorRange.sensorMin;
    yMax = sensorRange.sensorMax;
    yShift = yMax - 2.5;
    yShiftMargin = yMax - Region.yMax;
  } else {
    sensorRange = getMinMaxSensor(sensorMounting, Region.xMin, Region.xMax);
    xMin = sensorRange.sensorMin;
    xMax = sensorRange.sensorMax;
    yMin = 0;
    yMax = 4;
    xShift = xMax - 2;
  }

  const marginLeftM = isCeiling ? Math.abs(xMin - Region.xMin) : Math.abs(xMax - Region.xMax);
  const marginLeftPx = marginLeftM * M_TO_PX;
  const marginTopM = isCeiling ? Math.abs(yMax - Region.yMax) : 0;
  const marginTopPx = marginTopM * M_TO_PX;

  const containerStyle = isSensorValid
    ? {
      width: `${xSpan * M_TO_PX}px`,
      height: `${ySpan * M_TO_PX}px`,
      marginLeft: marginLeftPx,
      marginTop: marginTopPx,
      position: 'relative',
      backgroundColor: regionColor,
      border: '2px solid ' + regionBorderColor
    }
    : { display: 'none' };

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (active) {
      // Delay slightly to ensure layout is visible
      const timeout = setTimeout(() => {
        setRefreshKey(prev => prev + 1);
      }, 50); // tune if needed
      return () => clearTimeout(timeout);
    }
  }, [active, Region, Subregions]);

  return (
    <>
      <div key={refreshKey} className="bedroom-container" style={containerStyle}>

        {[...Subregions].reverse().map((item, i) => {
          const xSpanItem = item['xMax'] - item['xMin'];
          const ySpanItem = item['yMax'] - item['yMin'];
          const marginLeftItemM = isCeiling ? Math.abs(item['xMin'] - xMin) - marginLeftM 
                                            : Math.abs(xMax - item['xMax']) - marginLeftM ;
          const marginLeftItemPx = marginLeftItemM * M_TO_PX;
          const marginTopItemM = isCeiling ? Math.abs(item['yMax'] - yMax) - marginTopM 
                                           : item['yMin'];
          const marginTopItemPx = marginTopItemM * M_TO_PX;
          const originalIndex = Subregions.length - 1 - i;

          const x = marginLeftItemPx;
          const y = marginTopItemPx;
          const width = xSpanItem * M_TO_PX;
          const height = ySpanItem * M_TO_PX;

          return (
            <Rnd
              key={originalIndex}
              default={{ x, y, width, height }}
              bounds="parent"
              style={{
                backgroundColor: regionBooleans[originalIndex] ? subregionBorderColor : subregionColor,
                border: '2px solid ' + subregionBorderColor,
              }}
              // non riesco a trovare una spiegazione analitica per spiegare l'effetto di 2*yShift....
              // introdotto per risolvere drag/resize quando la y non è nel range da -2.5 a 2.5
              enableResizing={mod !== 0}
              onResizeStop={(e, direction, ref, delta, position) => {
                const updated = [...Subregions];
                const newWidth = ref.offsetWidth;
                const newHeight = ref.offsetHeight;
                let newXmin, newXmax;
                if (isCeiling) {
                  newXmin = (position.x / M_TO_PX) + marginLeftM + xMin;
                  newXmax = ((position.x + newWidth) / M_TO_PX) + marginLeftM + xMin;
                } else {
                  newXmin = xMax - ((position.x + newWidth) / M_TO_PX) - marginLeftM;
                  newXmax = xMax - (position.x / M_TO_PX) - marginLeftM;
                }
                let newYmin, newYmax;
                if (isCeiling) {
                  newYmin = ((pixelHeight - (position.y  + newHeight)) / M_TO_PX) - marginTopM - yMax + 2*yShift;
                  newYmax = ((pixelHeight - position.y) / M_TO_PX) - marginTopM - yMax + 2*yShift;
                } else {
                  newYmin = (position.y / M_TO_PX);
                  newYmax = ((position.y  + newHeight) / M_TO_PX);
                }
                updated[originalIndex] = {
                  ...updated[originalIndex],
                  xMin: Number(parseFloat(newXmin).toFixed(2)),
                  xMax: Number(parseFloat(newXmax).toFixed(2)),
                  yMin: Number(parseFloat(newYmin).toFixed(2)),
                  yMax: Number(parseFloat(newYmax).toFixed(2)),
                };
                setSubregions(updated);
              }}
              disableDragging={mod === 0}
              onDragStop={(e, d) => {
                const updated = [...Subregions];
                let newXmin, newXmax;
                if (isCeiling) {
                  newXmin = (d.x / M_TO_PX) + marginLeftM + xMin;
                  newXmax = xSpanItem + newXmin;
                } else {
                  newXmax = xMax - (d.x / M_TO_PX) - marginLeftM;
                  newXmin = newXmax - xSpanItem;
                }
                let newYmin, newYmax;
                if (isCeiling) {
                  newYmax = ((pixelHeight - d.y) / M_TO_PX) - marginTopM - yMax + 2*yShift;
                  newYmin = -ySpanItem + newYmax;
                } else {
                  newYmin = (d.y / M_TO_PX);
                  newYmax = newYmin + ySpanItem;
                }
                updated[originalIndex] = {
                  ...updated[originalIndex],
                  xMin: Number(parseFloat(newXmin).toFixed(2)),
                  xMax: Number(parseFloat(newXmax).toFixed(2)),
                  yMin: Number(parseFloat(newYmin).toFixed(2)),
                  yMax: Number(parseFloat(newYmax).toFixed(2)),
                };
                setSubregions(updated);
              }}

            >
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: (ySpanItem > xSpanItem) ? 'translate(-50%, -50%) rotate(-90deg)' : 'translate(-50%, -50%)',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  color: 'white'
                }}
              >
                {item['name']}
              </div>
            </Rnd>
          );

        })}

        {target !== undefined &&
          target.map((target, index) => (
            <Target
              key={index}
              target={target}
              xMin={isCeiling ? -2 : sensorRange.sensorMin}
              xMax={isCeiling ? 2 : sensorRange.sensorMax}
              yMin={isCeiling ? sensorRange.sensorMin : 0}
              yMax={isCeiling ? sensorRange.sensorMax : 4}
              marginLeft={marginLeftPx}
              marginTop={marginTopPx}
              M_TO_PX={M_TO_PX}
              isCeiling={isCeiling}
              yShift={yShift}
              yShiftMargin={yShiftMargin}
              xShift={xShift}
            />
          ))}

          {falls !== undefined &&
          falls.map((fall, index) => (
            <Fall
              key={index}
              fall={fall}
              xMin={isCeiling ? -2 : sensorRange.sensorMin}
              xMax={isCeiling ? 2 : sensorRange.sensorMax}
              yMin={isCeiling ? sensorRange.sensorMin : 0}
              yMax={isCeiling ? sensorRange.sensorMax : 4}
              marginLeft={marginLeftPx}
              marginTop={marginTopPx}
              M_TO_PX={M_TO_PX}
              isCeiling={isCeiling}
              yShift={yShift}
              yShiftMargin={yShiftMargin}
              xShift={xShift}
            />
          ))}

      </div>
    </>
  );
};

export default BedroomLayout;
